import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ShieldCheck, X } from 'lucide-react';
const NAMES = ['Martina G.', 'Lucas R.', 'Sofía M.', 'Mateo P.', 'Camila L.', 'Joaquín B.', 'Valentina S.', 'Tomás F.', 'Florencia D.', 'Benjamín A.', 'Catalina V.', 'Santiago O.', 'Agustina N.', 'Bautista C.', 'Renata H.', 'Facundo I.', 'Julieta E.', 'Nicolás Q.', 'Emilia K.', 'Federico T.', 'Micaela J.', 'Gonzalo U.', 'Antonella Z.', 'Ignacio W.', 'Brenda Y.', 'Maximiliano X.', 'Carolina B.', 'Sebastián M.', 'Daniela P.', 'Diego L.'];
const CITIES = ['CABA', 'La Plata, BA', 'Mar del Plata, BA', 'Bahía Blanca, BA', 'Tigre, BA', 'Quilmes, BA', 'San Isidro, BA', 'Lanús, BA', 'Avellaneda, BA', 'Córdoba Capital', 'Villa Carlos Paz, Córdoba', 'Río Cuarto, Córdoba', 'Rosario, Santa Fe', 'Santa Fe Capital', 'Rafaela, Santa Fe', 'Mendoza Capital', 'San Rafael, Mendoza', 'Godoy Cruz, Mendoza', 'San Miguel de Tucumán', 'Salta Capital', 'San Salvador de Jujuy', 'Resistencia, Chaco', 'Posadas, Misiones', 'Corrientes Capital', 'Neuquén Capital', 'Bariloche, Río Negro', 'Comodoro Rivadavia, Chubut', 'Ushuaia, Tierra del Fuego', 'Río Gallegos, Santa Cruz', 'La Rioja Capital', 'San Juan Capital', 'San Luis Capital', 'Paraná, Entre Ríos', 'Concordia, Entre Ríos', 'Santiago del Estero', 'Catamarca Capital', 'Formosa Capital'];
const PLANS = [{
  name: 'Plan Completo',
  price: '$19.990'
}, {
  name: 'KIT PRO',
  price: '$24.990'
}, {
  name: 'KIT PRO',
  price: '$24.990'
}, {
  name: 'KIT PRO',
  price: '$24.990'
}];
const TIMES = ['Hace 1 minuto', 'Hace 2 minutos', 'Hace 4 minutos', 'Hace 6 minutos', 'Hace pocos segundos'];
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
interface Purchase {
  id: number;
  name: string;
  city: string;
  plan: {
    name: string;
    price: string;
  };
  time: string;
}
function generatePurchase(id: number): Purchase {
  return {
    id,
    name: pick(NAMES),
    city: pick(CITIES),
    plan: pick(PLANS),
    time: pick(TIMES)
  };
}
export function PurchaseNotification() {
  const [current, setCurrent] = useState<Purchase | null>(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (dismissed) return;
    let visibleTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    let counter = 0;
    const showNotification = () => {
      counter += 1;
      setCurrent(generatePurchase(counter));
      // hide after ~6 seconds
      visibleTimer = setTimeout(() => {
        setCurrent(null);
        // schedule next between 40-60 seconds
        const nextDelay = 40000 + Math.random() * 20000;
        nextTimer = setTimeout(showNotification, nextDelay);
      }, 6000);
    };
    // first notification appears after ~8 seconds on page
    nextTimer = setTimeout(showNotification, 8000);
    return () => {
      clearTimeout(visibleTimer);
      clearTimeout(nextTimer);
    };
  }, [dismissed]);
  if (dismissed) return null;
  return <div className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-sm z-[60] pointer-events-none" data-id="element-345">
      <AnimatePresence data-id="element-346">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-3 flex items-start gap-3 relative overflow-hidden" role="status" aria-live="polite" data-id="element-347">
            {/* Accent bar */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-success" data-id="element-348"></div>

            {/* Avatar / icon */}
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/10 flex items-center justify-center relative" data-id="element-349">
              <ShieldCheck className="w-5 h-5 text-success" data-id="element-350" />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success rounded-full flex items-center justify-center border-2 border-white" data-id="element-351">
                <CheckCircle2 className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-352" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-4" data-id="element-353">
              <div className="text-[10px] font-bold text-success uppercase tracking-wider mb-0.5" data-id="element-354">
                ✓ Compra verificada
              </div>
              <p className="text-sm text-navy-900 leading-snug" data-id="element-355">
                <span className="font-bold" data-id="element-356">{current.name}</span>{' '}
                <span className="text-slate-600" data-id="element-357">de</span>{' '}
                <span className="font-semibold" data-id="element-358">{current.city}</span>{' '}
                <span className="text-slate-600" data-id="element-359">acaba de comprar</span>{' '}
                <span className="font-bold text-success" data-id="element-360">
                  {current.plan.name}
                </span>
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1" data-id="element-361">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" data-id="element-362"></span>
                {current.time} · {current.plan.price} ARS
              </p>
            </div>

            {/* Close */}
            <button type="button" aria-label="Cerrar notificación" onClick={() => setDismissed(true)} className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-363">
              <X className="w-3.5 h-3.5" data-id="element-364" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}